"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  Renumbered,
  RenumberMap,
} from "@/components/tools/reference-checkers/renumbering";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CitationFormat,
  checkCitationOrder,
  detectFormat,
  type OrderReport,
  type RenumberStatus,
} from "@/lib/tool-kit/reference-checkers/citation-order";
import type { ListRule } from "@/lib/tool-kit/reference-checkers/numbered-list";

/** English strings. A translated content file overrides these through `ui`. */
export const NUMBERED_CITATION_CHECKER_STRINGS = {
  textLabel: "Your paper’s text",
  textPlaceholder:
    "Paste the body of your paper with its numbered citations — [1], [2–4], (5) or superscripts.",
  listLabel: "Your numbered reference list (optional)",
  listPlaceholder: "[1] …\n[2] …\n[3] …",
  listHint:
    "Add the list to find references that are never cited and citations with no reference, and to get the list back in the new order.",
  formatLabel: "Citation format",
  formats: {
    auto: "Detect automatically",
    bracket: "Square brackets — [1], [2–4], [1, 3]",
    paren: "Parentheses — (1), (2-4), (1, 3)",
    superscript: "Superscripts — ¹, ^1, or a number right after a word",
  },
  formatNames: {
    bracket: "square brackets",
    paren: "parentheses",
    superscript: "superscripts",
  },
  detected: "Reading citations in {format}.",
  trySample: "Try a sample",
  clear: "Clear",
  sampleText:
    "Deep networks now dominate radar target recognition [1], [2]. Earlier systems relied on handcrafted features [5], and transfer learning helps when labelled data are scarce [3, p. 4]. Two surveys compare the approaches [2]–[4], and one public benchmark [7] has become the standard test [1].",
  sampleList:
    "[1] A. Author, “First source,” J. Example Stud., vol. 1, pp. 1–10, 2020.\n[2] B. Author, “Second source,” J. Example Stud., vol. 2, pp. 11–20, 2021.\n[3] C. Author, “Third source,” J. Example Stud., vol. 3, pp. 21–30, 2019.\n[4] D. Author, “Fourth source,” J. Example Stud., vol. 4, pp. 31–40, 2018.\n[5] E. Author, “Fifth source,” J. Example Stud., vol. 5, pp. 41–50, 2017.\n[6] F. Author, “Sixth source,” J. Example Stud., vol. 6, pp. 51–60, 2016.",
  emptyPrompt: "Paste your paper’s text to check the order of its citations.",
  noCitations:
    "No numbered citations were found in this format. Choose another citation format above.",
  results: "Results",
  statGroups: "Citations in the text",
  statCited: "Different references cited",
  statChanged: "References that change number",
  inOrder:
    "Every reference is first cited in numerical order, so no renumbering is needed.",
  issuesHeading: "What to fix",
  outOfOrder:
    "Reference {number} is first cited before {before}. Numbers should follow the order in which sources are first cited.",
  moreOutOfOrder:
    "one:{count} more reference is cited out of order.|other:{count} more references are cited out of order.",
  neverCited:
    "one:Reference {numbers} is in the list but never cited in the text.|other:References {numbers} are in the list but never cited in the text.",
  missingReferences:
    "one:Citation {numbers} has no entry in the reference list.|other:Citations {numbers} have no entry in the reference list.",
  skipped:
    "one:Number {numbers} is never cited — check whether a reference was removed.|other:Numbers {numbers} are never cited — check whether references were removed.",
  listRules: {
    unnumbered: "The reference list has no numbers.",
    "numbering-missing":
      "Some entries in the list have no number: {entries} (counting from the top).",
    "numbering-duplicate": "The list uses the number {number} twice.",
    "numbering-start": "The list starts at {first} rather than 1.",
    "numbering-gap": "The list skips {numbers}.",
    "numbering-order": "In the list, {number} comes after {previous}.",
    "duplicate-entry":
      "List entries {first} and {second} look like the same source.",
    "alphabetical-order": "The list is in alphabetical order.",
  } satisfies Record<ListRule, string>,
  mapHeading: "Renumbering map",
  mapHint:
    "Old number → new number, in order of first citation. References that are never cited move to the end.",
  colOld: "Old",
  colNew: "New",
  colStatus: "Status",
  statuses: {
    same: "Unchanged",
    moved: "Renumbered",
    "never-cited": "Never cited — cite it or remove it",
    "no-reference": "Cited, but not in the list",
  } satisfies Record<RenumberStatus, string>,
  renumberedTextHeading: "Your text, renumbered",
  renumberedListHeading: "Your reference list in the new order",
  renumberedTextLabel: "Text with citations renumbered",
  renumberedListLabel: "Reference list renumbered",
  copyText: "Copy text",
  copyList: "Copy list",
  copied: "Copied!",
  highlightHeading: "Citations found in your text",
  highlightLabel: "Your text with its citations highlighted",
  legendFirst: "Brings in a reference out of order",
  legendOther: "Citation",
  privacy:
    "Everything runs in your browser — your text and references are not uploaded.",
} as const;

type Strings = typeof NUMBERED_CITATION_CHECKER_STRINGS;
type FormatChoice = CitationFormat | "auto";

const TONES = { early: "amber", ok: "sky" } as const;

function numberText(numbers: readonly number[]): string {
  return numbers.join(", ");
}

/** "3 and 4", "3, 4 and 5" — joined the way the page's language joins a list. */
function joinedNumbers(numbers: readonly number[], locale: string): string {
  try {
    return new Intl.ListFormat(locale, { type: "conjunction" }).format(
      numbers.map(String)
    );
  } catch {
    return numberText(numbers);
  }
}

/** The sentences under "What to fix", in reading order. */
function issueLines(
  report: OrderReport,
  strings: Strings,
  locale: string,
  fmt: (value: number) => string
): string[] {
  const lines: string[] = [];
  const shown = report.outOfOrder.slice(0, 8);
  for (const issue of shown) {
    lines.push(
      t(strings.outOfOrder, {
        number: String(issue.number),
        before: joinedNumbers(issue.before, locale),
      })
    );
  }
  const more = report.outOfOrder.length - shown.length;
  if (more > 0) {
    lines.push(
      tPlural(strings.moreOutOfOrder, locale, more, { count: fmt(more) })
    );
  }
  const { missingReferences, neverCited, skipped } = report;
  if (missingReferences.length > 0) {
    lines.push(
      tPlural(strings.missingReferences, locale, missingReferences.length, {
        numbers: numberText(missingReferences),
      })
    );
  }
  if (neverCited.length > 0) {
    lines.push(
      tPlural(strings.neverCited, locale, neverCited.length, {
        numbers: numberText(neverCited),
      })
    );
  }
  if (skipped.length > 0) {
    lines.push(
      tPlural(strings.skipped, locale, skipped.length, {
        numbers: numberText(skipped),
      })
    );
  }
  for (const finding of report.listFindings) {
    const values: Record<string, string> = {};
    for (const [key, value] of Object.entries(finding.values ?? {})) {
      values[key] = String(value);
    }
    lines.push(t(strings.listRules[finding.rule], values));
  }
  return lines;
}

function highlightMatches(report: OrderReport): TextMatch[] {
  const early = new Set(report.outOfOrder.map((issue) => issue.number));
  const seen = new Set<number>();
  return report.groups.map((group) => {
    const firstHere = group.numbers.filter((n) => !seen.has(n));
    for (const n of group.numbers) {
      seen.add(n);
    }
    return {
      start: group.start,
      end: group.end,
      category: firstHere.some((n) => early.has(n)) ? "early" : "ok",
    };
  });
}

function Results({
  report,
  text,
  strings,
}: {
  report: OrderReport;
  text: string;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (report.groups.length === 0) {
    return <Notice>{strings.noCitations}</Notice>;
  }
  const issues = issueLines(report, strings, locale, fmt);
  const legend = [
    [TONES.early, strings.legendFirst],
    [TONES.ok, strings.legendOther],
  ] as const;
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          label={strings.statGroups}
          value={fmt(report.groups.length)}
        />
        <StatCard
          label={strings.statCited}
          value={fmt(report.firstCited.length)}
        />
        <StatCard
          emphasis
          label={strings.statChanged}
          value={fmt(report.changed)}
        />
      </StatGrid>
      {issues.length > 0 ? (
        <div className={`${CARD} space-y-3`}>
          <h3 className={WIDGET_H3}>{strings.issuesHeading}</h3>
          <ul className="list-disc space-y-2 pl-5 text-foreground text-sm leading-relaxed">
            {issues.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      ) : (
        <Notice>{strings.inOrder}</Notice>
      )}
      {report.changed > 0 ? (
        <>
          <RenumberMap
            labels={{
              heading: strings.mapHeading,
              hint: strings.mapHint,
              old: strings.colOld,
              new: strings.colNew,
              status: strings.colStatus,
              statuses: strings.statuses,
            }}
            rows={report.mapping}
          />
          <Renumbered
            copiedLabel={strings.copied}
            copyLabel={strings.copyText}
            heading={strings.renumberedTextHeading}
            id="nco-renumbered-text"
            label={strings.renumberedTextLabel}
            value={report.renumberedText}
          />
          {report.hasList ? (
            <Renumbered
              copiedLabel={strings.copied}
              copyLabel={strings.copyList}
              heading={strings.renumberedListHeading}
              id="nco-renumbered-list"
              label={strings.renumberedListLabel}
              value={report.renumberedList}
            />
          ) : null}
        </>
      ) : null}
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.highlightHeading}</h3>
        <ul className="flex flex-wrap gap-3 text-muted-foreground text-xs">
          {legend.map(([tone, label]) => (
            <li key={label}>
              <span className={HIGHLIGHT_TONES[tone]}>{label}</span>
            </li>
          ))}
        </ul>
        <HighlightedText
          label={strings.highlightLabel}
          matches={highlightMatches(report)}
          text={text}
          tones={TONES}
        />
      </div>
    </section>
  );
}

export default function NumberedCitationCheckerWidget() {
  const strings = useToolUi(NUMBERED_CITATION_CHECKER_STRINGS);
  const [text, setText] = useState("");
  const [list, setList] = useState("");
  const [choice, setChoice] = useState<FormatChoice>("auto");
  const deferredText = useDeferredValue(text);
  const deferredList = useDeferredValue(list);
  const format: CitationFormat =
    choice === "auto" ? detectFormat(deferredText) : choice;
  const report = useMemo(
    () =>
      deferredText.trim()
        ? checkCitationOrder(deferredText, deferredList, format)
        : null,
    [deferredText, deferredList, format]
  );
  const options = (["auto", "bracket", "paren", "superscript"] as const).map(
    (value) => ({ value, label: strings.formats[value] })
  );

  return (
    <>
      <TextAreaField
        id="nco-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <TextAreaField
        hint={strings.listHint}
        id="nco-list"
        label={strings.listLabel}
        onChange={setList}
        placeholder={strings.listPlaceholder}
        prose
        rows={6}
        value={list}
      />
      <SelectField
        hint={
          choice === "auto" && report
            ? t(strings.detected, { format: strings.formatNames[format] })
            : undefined
        }
        id="nco-format"
        label={strings.formatLabel}
        onChange={setChoice}
        options={options}
        value={choice}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sampleText);
            setList(strings.sampleList);
            setChoice("auto");
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0 && list.length === 0}
          onClick={() => {
            setText("");
            setList("");
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {report ? (
        <Results report={report} strings={strings} text={deferredText} />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
      <p className={HINT}>{strings.privacy}</p>
    </>
  );
}
